import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { VerificationType } from '@brighte/validation';
import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { usePasswordlessUserVerification, useTimer, useUpdateContactDetails } from '../../hooks';
import { useAuth0 } from '../../provider';
import { getResendWaitSeconds, getResendWaitSecondsForCount, incrementResendCodeCount } from '../../provider/cache';
import { LABELS, maskEmail } from '../../utils';
import { FormContactFieldsType, VerificationDetailsType } from '../ContactFields';
import SecureCode from './SecureCode';

export type VerifyFieldProps = {
  verificationDetails?: VerificationDetailsType;
  onRequestCode: (data: FormContactFieldsType) => void;
  financialProductId?: string;
  onSubmit: (data: FormContactFieldsType) => void;
  isRequesting: boolean;
  isSubmitting: boolean;
  isUniversalLogin?: boolean;
} & UseFormReturn<FormContactFieldsType>;

export const VerifyFields = ({
  verificationDetails,
  control,
  onRequestCode,
  financialProductId,
  watch,
  setFocus,
  handleSubmit,
  onSubmit,
  getValues,
  register,
  resetField,
  formState,
  isRequesting,
  isSubmitting,
  isUniversalLogin,
}: VerifyFieldProps) => {
  const { email, mobile, verificationType } = verificationDetails || {};
  const [otpSent, otp, updateOtpSent, newEmail, newMobile, updateOtp] = watch([
    'otpSent',
    'otp',
    'updateOtpSent',
    'newEmail',
    'newMobile',
    'updateOtp',
  ]);

  const { user } = useAuth0();
  const { createPasswordlessUser } = usePasswordlessUserVerification();
  const { sendUpdateContactDetailsVerification } = useUpdateContactDetails();

  const formValues = getValues();
  const isMobileEmail = verificationType === VerificationType.SmsEmail;

  const [type, setType] = useState<VerificationType>(
    verificationType === VerificationType.Email ? VerificationType.Email : VerificationType.Sms,
  );

  const { MAX_ATTEMPTS, UPDATE_MAX_ATTEMPTS } = LABELS;

  const userEmail = updateOtpSent ? newEmail : email;
  const userMobile = updateOtpSent ? newMobile : mobile;

  // If one of the user detail(mobile/email) is not matched, we want to update that field.
  // So we need to verify the other type of verification Type
  // If we have email matched to user, we want to verify mobile and vice versa.
  const verifyField = updateOtpSent
    ? verificationType === VerificationType.Email
      ? VerificationType.Sms
      : VerificationType.Email
    : type;

  const isMobile = verifyField === VerificationType.Sms;

  const [resendWaitSeconds, setResendWaitSeconds] = useState(() => getResendWaitSeconds());
  const { time, resetTimer, clearTimer } = useTimer({ max: resendWaitSeconds });
  const countingDown = time > 0;
  const resendDisabled = countingDown || isRequesting;

  const otpField = updateOtpSent ? 'updateOtp' : 'otp';
  const inputValue = updateOtpSent ? updateOtp : otp;

  const handleRequestCode = (requestType: VerificationType) => {
    resetField(otpField);
    setFocus(otpField);

    onRequestCode({
      ...formValues,
      email: userEmail ?? '',
      mobile: userMobile ?? '',
      verificationType: requestType,
      financialProductId,
    });
    const waitSeconds = getResendWaitSeconds();
    setResendWaitSeconds(waitSeconds);
    resetTimer(waitSeconds);
  };

  useEffect(() => {
    if (!otpSent && !user) {
      handleRequestCode(type);
    }
  }, [otpSent, user]);

  useEffect(() => {
    if (otpSent || updateOtpSent) {
      setFocus?.(otpField);
    }
  }, [otpSent, updateOtpSent, setFocus]);

  useEffect(() => {
    if ([UPDATE_MAX_ATTEMPTS, MAX_ATTEMPTS].includes(formState.errors[otpField]?.message ?? '')) {
      clearTimer();
    }
  }, [formState.errors[otpField]]);

  const handleVerificationTypeChange = () => {
    const requestType = type === VerificationType.Sms ? VerificationType.Email : VerificationType.Sms;
    setType(requestType);
    resetField(otpField);
    resetField('otpSent');
  };

  const handleResendCode = () => {
    if (countingDown) return;
    setFocus(otpField);
    resetField(otpField);

    const data = {
      email: userEmail ?? '',
      mobile: userMobile ?? '',
      verificationType: verifyField,
    };

    if (updateOtpSent) {
      sendUpdateContactDetailsVerification(data);
    } else {
      createPasswordlessUser({
        ...data,
        financialProductId,
      });
    }

    const nextCount = incrementResendCodeCount();
    const nextWaitSeconds = getResendWaitSecondsForCount(nextCount);
    setResendWaitSeconds(nextWaitSeconds);
    resetTimer(nextWaitSeconds);
  };

  const handleVerifySubmit = data => {
    // in usePasswordlessVerification, if verificationType is SmsEmail, we will only send via email to get the token.
    // since we have option now to verify by email for SmsEmail, this is to ensure we can also authenticate by email
    onSubmit({
      ...data,
      verificationType: type,
    });
  };

  return (
    <Stack gap="large">
      <Text size="xlarge">Verify your {isMobile ? 'mobile' : 'email'}</Text>
      <Text>
        Enter the 6-digit code that we sent to{' '}
        {isMobile ? `your mobile ending in ${userMobile?.slice(-4)}` : maskEmail(userEmail ?? '')}
      </Text>

      <Stack gap="large">
        <Box css={css({ maxWidth: 350 })}>
          <SecureCode control={control} {...register(otpField)} />
        </Box>
        <Button
          rounded
          css={css({ width: 200 })}
          onClick={handleSubmit(handleVerifySubmit)}
          disabled={!inputValue || isSubmitting}
          loading={isSubmitting}
        >
          Verify
        </Button>
      </Stack>
      <Box gap="large" display="flex" flex={1} flexDirection="column"></Box>
      <Stack gap="xlarge">
        <Text>
          <TextLinkButton aria-disabled={resendDisabled} onClick={handleResendCode} textDecoration="none">
            Didn't receive a code?
          </TextLinkButton>
        </Text>
        {resendDisabled && <Text>You can request another code in {time}s</Text>}
      </Stack>
      {isMobileEmail && !isUniversalLogin && (
        <Stack paddingY="xlarge">
          <Text>
            <TextLinkButton onClick={handleVerificationTypeChange} weight="regular">
              Verify with {type === VerificationType.Sms ? 'email' : 'mobile'} instead
            </TextLinkButton>
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
